import { useEffect, useState } from 'react';
import { BrandMark } from './BrandMark';
import { ApiError } from '../lib/api';
import type { BillingStatus, Plan, PlanInfo } from '../types';

interface PaywallScreenProps {
  email: string;
  billing: BillingStatus;
  checkout: (plan: Plan) => Promise<string>;
  fetchPlans: () => Promise<PlanInfo[]>;
  onSignOut: () => void;
}

function formatPrice(info: PlanInfo | undefined): string {
  if (!info) return '';
  const amount = (info.amount / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: info.currency.toUpperCase(),
    minimumFractionDigits: info.amount % 100 === 0 ? 0 : 2,
  });
  return `${amount}/${info.interval}`;
}

/**
 * The full-screen block shown in place of the app once an account's trial
 * has run out (or a subscription lapsed) with nothing active behind it.
 * Reached only when billing.hasAccess is false — everything else (an active
 * trial, an active or past_due subscription) renders the normal Workspace.
 */
export function PaywallScreen({ email, billing, checkout, fetchPlans, onSignOut }: PaywallScreenProps) {
  const [busyPlan, setBusyPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanInfo[] | null>(null);

  useEffect(() => {
    fetchPlans()
      .then(setPlans)
      .catch(() => setPlans([]));
  }, [fetchPlans]);

  const monthly = plans?.find((p) => p.plan === 'monthly');
  const annual = plans?.find((p) => p.plan === 'annual');

  async function subscribe(plan: Plan) {
    setBusyPlan(plan);
    setError(null);
    try {
      const url = await checkout(plan);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setBusyPlan(null);
    }
  }

  const headline = billing.status === 'canceled' ? 'Your subscription has ended' : 'Your trial has ended';

  return (
    <div className="auth-screen">
      <div className="auth-card paywall-card">
        <div className="brand" style={{ justifyContent: 'center', marginBottom: 14 }}>
          <BrandMark size={30} />
          <div className="brand-word">
            <span className="wordmark">Z3NTRAFY</span>
          </div>
        </div>

        <h2 className="paywall-headline">{headline}</h2>
        <p className="paywall-sub">
          Pick a plan to keep using Z3NTRAFY as <strong>{email}</strong>. Your tasks, connections, and settings are all
          still here — nothing is lost.
        </p>

        {error && <div className="auth-error">{error}</div>}

        <div className="plan-grid">
          <button
            className="plan-card"
            onClick={() => subscribe('monthly')}
            disabled={busyPlan !== null || plans === null}
          >
            <div className="plan-name">Monthly</div>
            <div className="plan-price">{plans === null ? <span className="spin" /> : formatPrice(monthly)}</div>
            <div className="plan-cta">{busyPlan === 'monthly' ? <span className="spin" /> : 'Subscribe'}</div>
          </button>
          <button
            className="plan-card featured"
            onClick={() => subscribe('annual')}
            disabled={busyPlan !== null || plans === null}
          >
            <div className="plan-badge">Best value</div>
            <div className="plan-name">Annual</div>
            <div className="plan-price">{plans === null ? <span className="spin" /> : formatPrice(annual)}</div>
            <div className="plan-cta">{busyPlan === 'annual' ? <span className="spin" /> : 'Subscribe'}</div>
          </button>
        </div>

        <p className="auth-switch">
          <button type="button" className="link" onClick={onSignOut}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
